"use client";

import React, { useState, useEffect, useRef } from 'react';
import { useClickOutsideHandler } from '@/hooks/useClickOutsideHandler';
import { X, Edit3 } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import useHeaderOffset from '@/hooks/useHeaderOffset';
import { useScrollLock } from '@/hooks/useScrollLock';
import { useContentSidebar } from '@/context/ContentSidebarContext';
import { useSession } from '@/context/SessionContext';
import NoteForm, { type NoteFormHandle } from './NoteForm';
import { usePullToClose } from '@/hooks/usePullToClose';
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';

export const CreateNoteSidebar: React.FC = () => {
  const { createNoteSidebarOpen, setCreateNoteSidebarOpen } = useContentSidebar();
  const { session, profile } = useSession();
  const [isClosing, setIsClosing] = useState(false);
  const [contentType, setContentType] = useState<'news' | 'promo'>('news');
  const sidebarRef = useRef<HTMLDivElement>(null);
  const noteFormRef = useRef<NoteFormHandle>(null);
  const { headerHeight, isDesktop, bottomNavHeight, recalculate } = useHeaderOffset();

  // Ref for the scrollable content area to support pull-to-close on mobile
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const handleClose = React.useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setCreateNoteSidebarOpen(false);
      setIsClosing(false);
    }, 200);
  }, [setCreateNoteSidebarOpen]);

  // Pull-to-close for mobile experience
  usePullToClose(scrollAreaRef, {
    onClose: handleClose,
    threshold: 80,
    enabled: createNoteSidebarOpen && !isDesktop
  });

  useScrollLock(createNoteSidebarOpen || isClosing, false, scrollAreaRef);

  // Track the sidebar open state to recalibrate its layout
  useEffect(() => {
    if (createNoteSidebarOpen) {
      recalculate();
      const timeoutId = setTimeout(recalculate, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [createNoteSidebarOpen, recalculate]);

  // Handle outside clicks to close the sidebar - usando hook reutilizable
  useClickOutsideHandler({
    isOpen: createNoteSidebarOpen,
    sidebarRef,
    handleClose,
  });

  // Cerrar con tecla ESC
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && createNoteSidebarOpen) {
        event.preventDefault();
        handleClose();
      }
    };

    if (createNoteSidebarOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [createNoteSidebarOpen, handleClose]);

  const handlePublishClick = async () => {
    if (noteFormRef.current) {
      await noteFormRef.current.submit();
    }
  };

  if (!createNoteSidebarOpen && !isClosing) {
    // Componente sigue renderizado pero oculto para preservar el estado
    return (
      <div style={{ display: 'none' }}>
        <NoteForm 
          key="create-note-form"
          ref={noteFormRef}
          session={session} 
          profile={profile} 
          hideCard={true}
          hideButton={true}
          onSuccess={() => {
            handleClose();
          }} 
        />
      </div>
    );
  }

  return (
    <>
      {/* Overlay */}
      {(createNoteSidebarOpen || isClosing) && (
        <div
          className={cn(
            'fixed inset-0 bg-transparent z-[9997] transition-opacity duration-200',
            isClosing && 'opacity-0 pointer-events-none'
          )}
          onClick={handleClose}
        />
      )}

      <div
        ref={sidebarRef}
        className={cn(
          'fixed right-0 w-full bg-background z-[9998]',
          'flex flex-col',
          'md:w-[500px]',
          'transition-transform duration-200 ease-in-out',
          createNoteSidebarOpen && !isClosing ? 'translate-x-0' : 'translate-x-full'
        )}
        style={{
          top: isDesktop ? headerHeight : 0,
          height: isDesktop 
            ? `calc(100vh - ${headerHeight}px)` 
            : '100dvh',
          bottom: isDesktop ? 0 : bottomNavHeight,
        }}
      >
        {/* Safe Area Spacer for Mobile */}
        {!isDesktop && <div className="h-[env(safe-area-inset-top,0px)] bg-primary w-full shrink-0" />}

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b shrink-0 bg-primary text-primary-foreground min-h-[64px]">
          <div className="flex items-center gap-2">
            <Edit3 className="w-5 h-5" />
            <div>
              <h2 className="text-xl font-bold">Escribir</h2>
            </div>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={(e) => { e.stopPropagation(); handleClose(); }}
                  className="p-1 hover:bg-white/10 text-white rounded-lg transition-colors hover:text-white"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </TooltipTrigger>
              <TooltipContent variant="sidebar">
                <p>Cerrar</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Toggle Noticia/Promo */}
        <div className="px-4 pt-4 pb-3 border-b bg-background">
          <div className="flex gap-2">
            <Button
              variant={contentType === 'news' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setContentType('news')}
              className="flex-1 flex flex-col items-center py-6 h-auto"
            >
              <span className="font-bold">Noticia</span>
              <span className="text-[10px] opacity-70 font-normal">📢 Comparte contenido</span>
            </Button>
            <Button
              variant={contentType === 'promo' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setContentType('promo')}
              className="flex-1 flex flex-col items-center py-6 h-auto relative"
            >
              <span className="font-bold">Promo</span>
              <span className="text-[10px] opacity-70 font-normal">🔥 Ganá comisiones</span>
              {contentType === 'promo' && (
                <Badge className="absolute -top-2 -right-2 bg-green-500 text-white text-xs h-5 px-1.5">
                  +$
                </Badge>
              )}
            </Button>
          </div>
        </div>

        {/* Content - Scrollable */}
        <ScrollArea 
          ref={scrollAreaRef}
          className="flex-1"
        >
          <div className="p-4">
            <NoteForm 
              key="create-note-form"
              ref={noteFormRef}
              session={session} 
              profile={profile} 
              hideCard={true}
              hideButton={true}
              proposedContentType={contentType}
              onSuccess={() => {
                handleClose();
              }} 
            />
          </div>
        </ScrollArea>

        {/* Footer - Fixed Button */}
        <div className="shrink-0 border-t bg-background p-4 space-y-3">
          <Button 
            onClick={handlePublishClick}
            disabled={noteFormRef.current?.isLoading}
            className="w-full"
            size="lg"
          >
            {noteFormRef.current?.isLoading ? 'Publicando...' : 'Publicar Nota'}
          </Button>
          <div className="h-[max(0px,env(safe-area-inset-bottom))]" />
        </div>
      </div>
    </>
  );
};
